  // Chart Helper Utilities for Sprint 7
// Provides data transformation and calculation functions for enhanced charts

import { Series, NAVHistory } from '../types';
import { format } from 'date-fns';

/**
 * Calculate returns percentage for a series
 */
export const calculateSeriesReturn = (series: Series): number => {
  if (series.principalAmount === 0) return 0;
  return ((series.currentNAV - series.principalAmount) / series.principalAmount) * 100;
};

/**
 * Calculate annualized return
 */
export const calculateAnnualizedReturn = (
  currentValue: number,
  initialValue: number,
  daysInvested: number
): number => {
  if (initialValue === 0 || daysInvested === 0) return 0;
  const totalReturn = (currentValue - initialValue) / initialValue;
  const years = daysInvested / 365;
  return (Math.pow(1 + totalReturn, 1 / years) - 1) * 100;
};

/**
 * Format chart data for multi-line performance chart
 * Combines NAV, Principal, Gains, and Fees into single data points
 */
export interface MultiLineChartData {
  date: string;
  dateObj: Date;
  nav: number;
  principal: number;
  gains: number;
  fees: number;
  returnPct: number;
}

export const formatMultiLineChartData = (
  navHistory: NAVHistory[],
  initialPrincipal: number = 0
): MultiLineChartData[] => {
  console.log('formatMultiLineChartData called with:', { historyLength: navHistory.length, initialPrincipal });
  
  if (!navHistory || navHistory.length === 0) {
    console.log('No nav history provided');
    return [];
  }

  // Sort by date ascending (oldest first) for proper chronological display
  const sortedHistory = [...navHistory].sort((a, b) => {
    const dateA = a.date instanceof Date ? a.date : new Date(a.date);
    const dateB = b.date instanceof Date ? b.date : new Date(b.date);
    return dateA.getTime() - dateB.getTime();
  });

  return sortedHistory.map((nav, index) => {
    const principal = initialPrincipal;
    const gains = nav.totalNAV - principal;
    const returnPct = principal > 0 ? (gains / principal) * 100 : 0;

    // Parse date - handle both Date objects and Timestamp objects
    let dateObj: Date;
    if (nav.date instanceof Date) {
      dateObj = nav.date;
    } else if (nav.date && typeof nav.date === 'object' && 'toDate' in nav.date) {
      // Firestore Timestamp
      dateObj = (nav.date as any).toDate();
    } else if (typeof nav.date === 'string' || typeof nav.date === 'number') {
      dateObj = new Date(nav.date);
    } else {
      console.warn('Invalid date format for nav:', nav);
      dateObj = new Date();
    }

    const dateStr = format(dateObj, 'MMM dd');
    console.log(`Point ${index}: date=${dateStr}, nav=${nav.totalNAV}`);

    return {
      date: dateStr,
      dateObj: dateObj,
      nav: nav.totalNAV,
      principal: principal,
      gains: gains,
      fees: 0,
      returnPct: returnPct,
    };
  });
};

/**
 * Calculate cumulative returns over time for area chart
 */
export interface ReturnsChartData {
  date: string;
  returnPct: number;
  isPositive: boolean;
}

export const calculateReturnsOverTime = (
  navHistory: NAVHistory[],
  initialValue: number
): ReturnsChartData[] => {
  if (navHistory.length === 0 || initialValue === 0) return [];

  return navHistory.map((nav) => {
    const returnPct = ((nav.totalNAV - initialValue) / initialValue) * 100;
    return {
      date: format(new Date(nav.date), 'MMM dd'),
      returnPct: returnPct,
      isPositive: returnPct >= 0,
    };
  });
};

/**
 * Prepare series comparison data for bar/column chart
 */
export interface SeriesComparisonData {
  seriesId: string;
  seriesLabel: string;
  returnPct: number;
  currentNAV: number;
  principal: number;
  lenderName: string;
  issueDate: string;
  daysActive: number;
  performanceTier: 'excellent' | 'good' | 'moderate' | 'negative';
  color: string;
}

export const prepareSeriesComparisonData = (
  series: Series[],
  lenders: any[]
): SeriesComparisonData[] => {
  const activeSeries = series.filter(s => s.status === 'active');
  
  return activeSeries.map((s) => {
    const returnPct = calculateSeriesReturn(s);
    const lender = lenders.find(l => l.id === s.lenderId);
    const daysActive = Math.floor((Date.now() - new Date(s.issueDate).getTime()) / (1000 * 60 * 60 * 24));
    
    // Performance tier classification
    let performanceTier: 'excellent' | 'good' | 'moderate' | 'negative';
    let color: string;
    
    if (returnPct > 15) {
      performanceTier = 'excellent';
      color = '#4CAF50'; // Green
    } else if (returnPct > 5) {
      performanceTier = 'good';
      color = '#81C784'; // Light green
    } else if (returnPct >= 0) {
      performanceTier = 'moderate';
      color = '#FFD700'; // Yellow
    } else {
      performanceTier = 'negative';
      color = '#F44336'; // Red
    }

    return {
      seriesId: s.id,
      seriesLabel: `${lender?.fullName || 'Unknown'} - ${format(new Date(s.issueDate), 'MMM yyyy')}`,
      returnPct: returnPct,
      currentNAV: s.currentNAV,
      principal: s.principalAmount,
      lenderName: lender?.fullName || 'Unknown',
      issueDate: format(new Date(s.issueDate), 'dd MMM yyyy'),
      daysActive: daysActive,
      performanceTier: performanceTier,
      color: color,
    };
  }).sort((a, b) => b.returnPct - a.returnPct); // Sort by performance descending
};

/**
 * Calculate portfolio statistics
 */
export interface PortfolioStats {
  averageReturn: number;
  bestPerformingSeries: string;
  worstPerformingSeries: string;
  totalSeries: number;
  averageDaysActive: number;
  volatility: number; // Standard deviation of returns
}

export const calculatePortfolioStats = (series: Series[]): PortfolioStats => {
  const activeSeries = series.filter(s => s.status === 'active');
  
  if (activeSeries.length === 0) {
    return {
      averageReturn: 0,
      bestPerformingSeries: 'N/A',
      worstPerformingSeries: 'N/A',
      totalSeries: 0,
      averageDaysActive: 0,
      volatility: 0,
    };
  }

  const returns = activeSeries.map(s => calculateSeriesReturn(s));
  const averageReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
  
  const bestReturn = Math.max(...returns);
  const worstReturn = Math.min(...returns);
  
  const bestSeries = activeSeries[returns.indexOf(bestReturn)];
  const worstSeries = activeSeries[returns.indexOf(worstReturn)];
  
  const daysActive = activeSeries.map(s =>
    Math.floor((Date.now() - new Date(s.issueDate).getTime()) / (1000 * 60 * 60 * 24))
  );
  const averageDaysActive = daysActive.reduce((sum, d) => sum + d, 0) / daysActive.length;
  
  // Calculate volatility (standard deviation of returns)
  const variance = returns.reduce((sum, r) => sum + Math.pow(r - averageReturn, 2), 0) / returns.length;
  const volatility = Math.sqrt(variance);

  return {
    averageReturn: averageReturn,
    bestPerformingSeries: bestSeries.id.slice(0, 8),
    worstPerformingSeries: worstSeries.id.slice(0, 8),
    totalSeries: activeSeries.length,
    averageDaysActive: Math.round(averageDaysActive),
    volatility: volatility,
  };
};

/**
 * Export chart data to CSV
 */
export const exportToCSV = (data: any[], filename: string): void => {
  if (data.length === 0) {
    alert('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(row => headers.map(header => {
      const value = row[header];
      // Handle values that might contain commas
      return typeof value === 'string' && value.includes(',') 
        ? `"${value}"` 
        : value;
    }).join(','))
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Generate color based on performance
 */
export const getPerformanceColor = (returnPct: number): string => {
  if (returnPct > 15) return '#4CAF50'; // Excellent (dark green)
  if (returnPct > 5) return '#81C784'; // Good (light green)
  if (returnPct >= 0) return '#FFD700'; // Moderate (yellow)
  return '#F44336'; // Negative (red)
};

/**
 * Format large numbers for charts (e.g., 1000000 -> 1.0M)
 */
export const formatChartNumber = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
};

/**
 * Calculate days between two dates
 */
export const daysBetween = (date1: Date | string, date2: Date | string): number => {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calculate Sharpe Ratio (risk-adjusted return)
 * Assumes risk-free rate of 3% annually
 */
export const calculateSharpeRatio = (
  returns: number[],
  riskFreeRate: number = 3
): number => {
  if (returns.length === 0) return 0;
  
  const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
  const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length;
  const stdDev = Math.sqrt(variance);
  
  if (stdDev === 0) return 0;
  
  return (avgReturn - riskFreeRate) / stdDev;
};
